import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { CLIENT_TYPE_LABELS } from '@/lib/clients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { Client, ClientType } from '@logistech/ui-shared';

// Aligné sur le schema Zod du missions-service (routes/clients/index.ts)
const schema = z.object({
  name: z.string().trim().min(2, 'Nom requis (2 caractères min.)').max(100),
  type: z.enum(['MINING', 'BTP', 'AGRO', 'TRANSIT', 'OTHER']),
  contactName: z.string().max(100),
  phone: z.string().max(30),
  email: z.union([z.literal(''), z.string().email('Email invalide')]),
  address: z.string().max(200),
  vatNumber: z.string().max(50),
  notes: z.string(),
});

export type ClientFormData = z.infer<typeof schema>;

const OPTIONAL_FIELDS = ['contactName', 'phone', 'email', 'address', 'vatNumber', 'notes'] as const;

/** Champs vides → undefined (création) ou null (modification, pour effacer la valeur). */
export const toClientPayload = (data: ClientFormData, emptyAs: undefined | null) => {
  const payload: Record<string, unknown> = { name: data.name, type: data.type };
  for (const f of OPTIONAL_FIELDS) {
    const v = data[f].trim();
    payload[f] = v === '' ? emptyAs : v;
  }
  return payload;
};

interface Props {
  initial?: Client;
  submitLabel: string;
  pending: boolean;
  onSubmit: (data: ClientFormData) => void;
  onCancel?: () => void;
}

export function ClientForm({ initial, submitLabel, pending, onSubmit, onCancel }: Props) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClientFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial?.name ?? '',
      type: initial?.type ?? 'MINING',
      contactName: initial?.contactName ?? '',
      phone: initial?.phone ?? '',
      email: initial?.email ?? '',
      address: initial?.address ?? '',
      vatNumber: initial?.vatNumber ?? '',
      notes: initial?.notes ?? '',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Nom *</Label>
          <Input id="name" {...register('name')} placeholder="Société Minière de Boké" />
          {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
        </div>
        <div>
          <Label htmlFor="type">Secteur</Label>
          <Select value={watch('type')} onValueChange={(v) => setValue('type', v as ClientType)}>
            <SelectTrigger id="type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(CLIENT_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="contactName">Nom du contact</Label>
          <Input id="contactName" {...register('contactName')} placeholder="Aissatou Sow" />
        </div>
        <div>
          <Label htmlFor="phone">Téléphone</Label>
          <Input id="phone" {...register('phone')} placeholder="+224 622 00 00 00" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" {...register('email')} placeholder="contact@client.gn" />
          {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
        </div>
        <div>
          <Label htmlFor="vatNumber">N° fiscal (NIF)</Label>
          <Input id="vatNumber" {...register('vatNumber')} />
        </div>
        <div className="col-span-2">
          <Label htmlFor="address">Adresse</Label>
          <Input id="address" {...register('address')} placeholder="Kaloum, Conakry" />
        </div>
        <div className="col-span-2">
          <Label htmlFor="notes">Notes</Label>
          <textarea
            autoComplete="off"
            id="notes"
            {...register('notes')}
            rows={3}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && <Button variant="outline" type="button" onClick={onCancel}>Annuler</Button>}
        <Button type="submit" disabled={pending}>{pending ? 'Enregistrement…' : submitLabel}</Button>
      </div>
    </form>
  );
}
